import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Wrench, Plus, Pencil, Trash2, AlertTriangle, MapPin, Package, Settings, Calendar, Building, List, FileBox } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useFormValidation } from "../hooks/useFormValidation";
import { InputWithError } from "../components/common/InputWithError";
import { useTranslation } from "../contexts/I18nContext";
import { AssetFormDialog, AssetDeleteDialog, AssetTipologieDialog } from "./AssetDialogs";

export default function AssetPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditAsset = canEdit("asset");
  
  const [asset, setAsset] = useState([]);
  const [centriCosto, setCentriCosto] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  
  // Loading states for actions
  const [savingAsset, setSavingAsset] = useState(false);
  const [savingTipologia, setSavingTipologia] = useState(false);
  const [deletingAsset, setDeletingAsset] = useState(false);
  const [deletingTipologia, setDeletingTipologia] = useState(null);
  
  // Filtro vista: "tutti" o nome della tipologia
  const [vistaAttiva, setVistaAttiva] = useState("tutti");
  
  // Tipologie dialog
  const [tipologieDialog, setTipologieDialog] = useState(false);
  const [tipologiaForm, setTipologiaForm] = useState({ nome: "", descrizione: "" });
  const [editingTipologia, setEditingTipologia] = useState(null);
  
  const [form, setForm] = useState({
    tipologia: "",
    marca: "",
    modello: "",
    matricola: "",
    fornitore: "",
    data_acquisto: "",
    fattura_riferimento: "",
    centro_costo_id: "",
    stato: "operativa",
    note: "",
    n_asset_interno: "",
    scadenza_taratura: ""
  });

  // Validazione form asset
  const { errors: assetErrors, validate: validateAsset, validateField: validateAssetField, clearErrors: clearAssetErrors } = useFormValidation({
    tipologia: { required: true },
    marca: { required: true },
    modello: { required: true }
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [assetRes, ccRes, tipRes] = await Promise.all([
        api.get(`${prefix}/asset`),
        api.get(`${prefix}/cost-centers`),
        api.get(`${prefix}/asset/tipologie`).catch(() => ({ data: [] }))
      ]);
      setAsset(assetRes.data);
      setCentriCosto(ccRes.data);
      setTipologie(tipRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      tipologia: "",
      marca: "",
      modello: "",
      matricola: "",
      fornitore: "",
      data_acquisto: "",
      fattura_riferimento: "",
      centro_costo_id: "",
      stato: "operativa",
      note: "",
      n_asset_interno: "",
      scadenza_taratura: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      tipologia: item.tipologia || "",
      marca: item.marca || "",
      modello: item.modello || "",
      matricola: item.matricola || "",
      fornitore: item.fornitore || "",
      data_acquisto: item.data_acquisto || "",
      fattura_riferimento: item.fattura_riferimento || "",
      centro_costo_id: item.centro_costo_id || "",
      stato: item.stato || "operativa",
      note: item.note || "",
      n_asset_interno: item.n_asset_interno || "",
      scadenza_taratura: item.scadenza_taratura || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    // Validazione client-side
    if (!validateAsset(form)) {
      return;
    }
    
    setSavingAsset(true);
    try {
      const params = new URLSearchParams({
        tipologia: form.tipologia,
        marca: form.marca,
        modello: form.modello,
        stato: form.stato
      });
      
      if (form.matricola) params.append("matricola", form.matricola);
      if (form.fornitore) params.append("fornitore", form.fornitore);
      if (form.data_acquisto) params.append("data_acquisto", form.data_acquisto);
      if (form.fattura_riferimento) params.append("fattura_riferimento", form.fattura_riferimento);
      if (form.centro_costo_id && form.centro_costo_id !== "__none__") {
        params.append("centro_costo_id", form.centro_costo_id);
      }
      if (form.note) params.append("note", form.note);
      if (form.n_asset_interno) params.append("n_asset_interno", form.n_asset_interno);
      if (form.scadenza_taratura) params.append("scadenza_taratura", form.scadenza_taratura);
      
      const prefix = getApiPrefix();
      if (editingId) {
        await api.put(`${prefix}/asset/${editingId}?${params.toString()}`);
        showSuccess(t("success.assetUpdated"));
      } else {
        await api.post(`${prefix}/asset?${params.toString()}`);
        showSuccess(t("success.assetCreated"));
      }
      
      setDialogOpen(false);
      clearAssetErrors();
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSavingAsset(false);
    }
  };

  const handleDelete = async () => {
    setDeletingAsset(true);
    try {
      await api.delete(`${getApiPrefix()}/asset/${deleteDialog.item.id}`);
      showSuccess(t("success.assetDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingAsset(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/asset/${id}`);
        deleted++;
      } catch (err) {
        
      }
    }
    showSuccess(`${deleted} asset eliminati`);
    fetchData();
  };

  // Tipologie handlers
  const handleAddTipologia = async () => {
    if (!tipologiaForm.nome.trim()) {
      handleApiError({ message: t("errors.requiredName") });
      return;
    }
    try {
      const params = new URLSearchParams({ nome: tipologiaForm.nome });
      if (tipologiaForm.descrizione) params.append("descrizione", tipologiaForm.descrizione);
      
      const prefix = getApiPrefix();
      if (editingTipologia) {
        await api.put(`${prefix}/asset/tipologie/${editingTipologia.id}?${params.toString()}`);
        showSuccess(t("success.typeUpdated"));
      } else {
        await api.post(`${prefix}/asset/tipologie?${params.toString()}`);
        showSuccess(t("success.typeCreated"));
      }
      setTipologiaForm({ nome: "", descrizione: "" });
      setEditingTipologia(null);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDeleteTipologia = async (tip) => {
    setDeletingTipologia(tip.id);
    try {
      await api.delete(`${getApiPrefix()}/asset/tipologie/${tip.id}`);
      showSuccess(t("success.typeDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingTipologia(null);
    }
  };

  // Stats
  const totaleAsset = asset.length;
  const assetOperativi = asset.filter(a => a.stato === 'operativa').length;
  const assetInManutenzione = asset.filter(a => a.stato === 'in_manutenzione').length;
  const assetConTaraturaScaduta = asset.filter(a => {
    if (!a.scadenza_taratura) return false;
    return new Date(a.scadenza_taratura) < new Date();
  }).length;

  // Riepilogo per CDC
  const riepilogoPerCdc = useMemo(() => {
    const stats = {};
    asset.forEach(a => {
      const cdcId = a.centro_costo_id || "senza_cdc";
      const cdcNome = a.centro_costo_nome || "Senza Locazione";
      if (!stats[cdcId]) {
        stats[cdcId] = {
          id: cdcId,
          nome: cdcNome,
          totale: 0,
          operativi: 0,
          inManutenzione: 0
        };
      }
      stats[cdcId].totale += 1;
      if (a.stato === 'operativa') stats[cdcId].operativi += 1;
      if (a.stato === 'in_manutenzione') stats[cdcId].inManutenzione += 1;
    });
    return Object.values(stats).sort((a, b) => {
      if (a.id === "senza_cdc") return 1;
      if (b.id === "senza_cdc") return -1;
      return a.nome.localeCompare(b.nome);
    });
  }, [asset]);

  // Riepilogo per Tipologia
  const riepilogoPerTipologia = useMemo(() => {
    const stats = {};
    asset.forEach(a => {
      const tipologia = a.tipologia || "Altro";
      if (!stats[tipologia]) {
        stats[tipologia] = {
          nome: tipologia,
          totale: 0,
          operativi: 0,
          inManutenzione: 0
        };
      }
      stats[tipologia].totale += 1;
      if (a.stato === 'operativa') stats[tipologia].operativi += 1;
      if (a.stato === 'in_manutenzione') stats[tipologia].inManutenzione += 1;
    });
    return Object.values(stats).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [asset]);

  // Asset filtrati in base alla vista attiva
  const assetFiltrati = useMemo(() => {
    if (vistaAttiva === "tutti") {
      return asset;
    }
    return asset.filter(a => (a.tipologia || "").toLowerCase() === vistaAttiva.toLowerCase());
  }, [asset, vistaAttiva]);

  // Statistiche per la vista corrente
  const statsFiltrate = useMemo(() => {
    const data = assetFiltrati;
    return {
      totale: data.length,
      operativi: data.filter(a => a.stato === 'operativa').length,
      inManutenzione: data.filter(a => a.stato === 'in_manutenzione').length,
      taraturaScaduta: data.filter(a => {
        if (!a.scadenza_taratura) return false;
        return new Date(a.scadenza_taratura) < new Date();
      }).length
    };
  }, [assetFiltrati]);

  // Colonne per DataTable
  const columns = [
    {
      key: 'n_asset_interno',
      label: 'N° Asset',
      render: (value) => value ? (
        <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{value}</span>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'tipologia',
      label: 'Tipologia',
      render: (value) => <Badge variant="outline" className="max-w-[120px] truncate" title={value}>{value}</Badge>
    },
    {
      key: 'marca',
      label: t('vehicles.brand'),
      render: (value) => <span className="font-medium truncate max-w-[100px] block" title={value}>{value}</span>
    },
    {
      key: 'modello',
      label: t('vehicles.model'),
      render: (value) => <span className="truncate max-w-[120px] block" title={value || "-"}>{value || "-"}</span>
    },
    {
      key: 'matricola',
      label: 'Matricola',
      render: (value) => value ? (
        <span className="font-mono text-xs truncate max-w-[100px] block" title={value}>{value}</span>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'fornitore',
      label: t('common.supplier'),
      render: (value) => <span className="truncate max-w-[100px] block" title={value || "-"}>{value || "-"}</span>
    },
    {
      key: 'data_acquisto',
      label: 'Data Acq.',
      render: (value) => value || <span className="text-muted-foreground">-</span>
    },
    {
      key: 'scadenza_taratura',
      label: 'Scad. Taratura',
      render: (value) => {
        if (!value) return <span className="text-muted-foreground">-</span>;
        const isExpired = new Date(value) < new Date();
        return (
          <span className={isExpired ? "text-red-600 font-medium" : ""}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'centro_costo_nome',
      label: 'Locazione',
      render: (value) => value ? (
        <div className="flex items-center gap-1 max-w-[100px]" title={value}>
          <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <span className="truncate">{value}</span>
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'stato',
      label: t('common.status'),
      render: (value) => {
        if (value === 'operativa') {
          return <Badge className="bg-green-100 text-green-700">Operativa</Badge>;
        } else if (value === 'in_manutenzione') {
          return <Badge className="bg-yellow-100 text-yellow-700">In Manut.</Badge>;
        }
        return <Badge variant="outline">{value}</Badge>;
      }
    },
    {
      key: 'stato_contabile',
      label: 'Contabile',
      render: (value, row) => {
        if (value === 'in_ammortamento') {
          return (
            <Badge className="bg-purple-100 text-purple-700">
              In Ammort.
            </Badge>
          );
        } else if (value === 'ammortizzato') {
          return (
            <Badge className="bg-blue-100 text-blue-700">
              Ammortizzato
            </Badge>
          );
        } else if (row.fattura_id || row.fattura_riferimento) {
          return (
            <Badge className="bg-orange-100 text-orange-700">
              Con Fattura
            </Badge>
          );
        }
        return <Badge variant="outline" className="text-muted-foreground">-</Badge>;
      }
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("nav.assetList")}</h1>
          <p className="text-muted-foreground text-sm">Gestisci attrezzature e macchinari aziendali</p>
        </div>
        <Button variant="outline" onClick={() => setTipologieDialog(true)}>
          <Settings className="h-4 w-4 mr-2" />
          Gestisci Tipologie
        </Button>
      </div>

      {/* Toggle Vista - Tabs per Tipologia */}
      <div className="flex flex-wrap gap-2 p-1 bg-muted/50 rounded-lg" data-testid="vista-toggle">
        <button
          onClick={() => setVistaAttiva("tutti")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            vistaAttiva === "tutti"
              ? "bg-white shadow-sm text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-white/50"
          }`}
          data-testid="vista-tutti"
        >
          <List className="h-4 w-4" />{t('common.all')}<Badge variant="secondary" className="ml-1 text-xs">{asset.length}</Badge>
        </button>
        {tipologie.map((tip) => {
          const count = asset.filter(a => (a.tipologia || "").toLowerCase() === tip.nome.toLowerCase()).length;
          return (
            <button
              key={tip.id}
              onClick={() => setVistaAttiva(tip.nome)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                vistaAttiva === tip.nome
                  ? "bg-white shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/50"
              }`}
              data-testid={`vista-${tip.nome.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <FileBox className="h-4 w-4" />
              Registro {tip.nome}
              <Badge variant="secondary" className="ml-1 text-xs">{count}</Badge>
            </button>
          );
        })}
      </div>

      {/* Stats - Dinamiche in base alla vista */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="overflow-hidden">
          <CardContent className="pt-6 card-content-responsive">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="card-subtitle-responsive text-muted-foreground uppercase">
                  {vistaAttiva === "tutti" ? "Totale Asset" : `Totale ${vistaAttiva}`}
                </p>
                <p className="stat-number">{statsFiltrate.totale}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="pt-6 card-content-responsive">
            <div className="flex items-center gap-3">
              <Wrench className="h-8 w-8 text-green-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="card-subtitle-responsive text-muted-foreground uppercase">Operativi</p>
                <p className="stat-number text-green-600">{statsFiltrate.operativi}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="pt-6 card-content-responsive">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-yellow-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="card-subtitle-responsive text-muted-foreground uppercase">In Manutenzione</p>
                <p className="stat-number text-yellow-600">{statsFiltrate.inManutenzione}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="pt-6 card-content-responsive">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="card-subtitle-responsive text-muted-foreground uppercase">Taratura Scaduta</p>
                <p className="stat-number text-red-600">{statsFiltrate.taraturaScaduta}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riepilogo per Centro di Costo */}
      {riepilogoPerCdc.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="h-5 w-5 text-indigo-500" />{t('common.summaryByCostCenter')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {riepilogoPerCdc.map((cdc) => {
                const manutenzioneRate = cdc.totale > 0 ? Math.round((cdc.inManutenzione / cdc.totale) * 100) : 0;
                const colorClass = manutenzioneRate >= 50 ? 'border-red-200 bg-red-50' : 
                                   manutenzioneRate >= 25 ? 'border-orange-200 bg-orange-50' : 
                                   'border-green-200 bg-green-50';
                return (
                  <div key={cdc.id} className={`p-4 rounded-lg border ${colorClass}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-indigo-500" />
                        {cdc.nome}
                      </h4>
                      <Badge variant="outline" className="text-xs">
                        {cdc.totale} asset
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <Wrench className="h-3 w-3 text-green-500" />
                        <span className="text-muted-foreground">Operativi:</span>
                        <span className="font-medium text-green-600">{cdc.operativi}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-yellow-500" />
                        <span className="text-muted-foreground">Manut.:</span>
                        <span className="font-medium text-yellow-600">{cdc.inManutenzione}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {vistaAttiva === "tutti" ? "Elenco Asset" : `Registro ${vistaAttiva}`}
          </CardTitle>
          <CardDescription>
            {vistaAttiva === "tutti" 
              ? "Clicca su un asset per modificarlo" 
              : `${assetFiltrati.length} asset di tipo "${vistaAttiva}"`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={assetFiltrati}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage={vistaAttiva === "tutti" 
              ? "Nessun asset registrato" 
              : `Nessun asset di tipo "${vistaAttiva}" registrato`}
            exportFileName={vistaAttiva === "tutti" 
              ? "asset_aziendali" 
              : `registro_${vistaAttiva.toLowerCase().replace(/\s+/g, '_')}`}
            toolbar={
              canEditAsset && (
                <Button onClick={openAddDialog} data-testid="add-asset-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Asset
                </Button>
              )
            }
          />
        </CardContent>
      </Card>

      {/* Add/Edit Asset Dialog */}
      <AssetFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editingId={editingId}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        saving={savingAsset}
        tipologie={tipologie}
        centriCosto={centriCosto}
        assetErrors={assetErrors}
        validateAssetField={validateAssetField}
        t={t}
      />

      <AssetDeleteDialog
        open={deleteDialog.open}
        item={deleteDialog.item}
        onClose={() => setDeleteDialog({ open: false, item: null })}
        onDelete={handleDelete}
        deleting={deletingAsset}
        t={t}
      />

      <AssetTipologieDialog
        open={tipologieDialog}
        onClose={() => setTipologieDialog(false)}
        tipologie={tipologie}
        tipologiaForm={tipologiaForm}
        setTipologiaForm={setTipologiaForm}
        editingTipologia={editingTipologia}
        setEditingTipologia={setEditingTipologia}
        onAdd={handleAddTipologia}
        onDelete={handleDeleteTipologia}
        t={t}
      />
    </div>
  );
}
